// ============================================================
// Nav — sticky top navigation, dark spine
//
// Spec: apps/marketing/DESIGN.md §6.1 + recalibration (dark + signal accent)
// - 64px tall, ink bg
// - Wordmark left, anchor links centre, signal-mint CTA right
// - Gains a 1px ink-3 bottom border + backdrop-blur once scrollY > 8
// - Mobile: anchor links collapse into a hamburger sheet
// - On /request-access the CTA hides and is replaced with "Sign in →"
// ============================================================

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import Wordmark from './Wordmark';

const ANCHOR_LINKS = [
  { label: 'Product', href: '/#product' },
  { label: 'Channels', href: '/#channels' },
  { label: 'How it works', href: '/#how-it-works' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'FAQ', href: '/#faq' },
];

function MenuIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [location] = useLocation();
  const onRequestPage = location === '/request-access';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <header
      className={`sticky top-0 z-40 h-16 w-full bg-ink/90 transition-all ${
        scrolled ? 'border-b border-ink-3 backdrop-blur-md' : ''
      }`}
    >
      <div className="mx-auto flex h-full max-w-content items-center justify-between px-6 md:px-12">
        <Link href="/" className="flex items-center" onClick={() => setMobileOpen(false)}>
          <Wordmark inverted className="text-base md:text-lg" />
        </Link>

        <nav className="hidden items-center gap-9 md:flex">
          {ANCHOR_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-ash transition-colors hover:text-white"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:block">
          {onRequestPage ? (
            <a href="/" className="text-sm font-medium text-ash hover:text-white">
              Sign in →
            </a>
          ) : (
            <Link
              href="/request-access"
              className="inline-flex items-center rounded-pill px-5 py-2.5 text-sm font-semibold transition-all hover:brightness-110 hover:shadow-[0_0_24px_rgba(124,255,184,0.45)]"
              style={{ background: '#7CFFB8', color: '#0E1316' }}
            >
              Request access
            </Link>
          )}
        </div>

        <button
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          className="text-white md:hidden"
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <CloseIcon /> : <MenuIcon />}
        </button>
      </div>

      {mobileOpen && (
        <div className="fixed inset-x-0 top-16 z-30 h-[calc(100vh-4rem)] overflow-y-auto bg-ink md:hidden">
          <nav className="mx-auto flex max-w-content flex-col gap-1 p-6">
            {ANCHOR_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMobileOpen(false)}
                className="rounded-lg px-3 py-3 text-base font-medium text-chalk hover:bg-ink-2"
              >
                {l.label}
              </a>
            ))}
            <div className="mt-4 border-t border-ink-3 pt-4">
              {onRequestPage ? (
                <a
                  href="/"
                  onClick={() => setMobileOpen(false)}
                  className="block rounded-lg px-3 py-3 text-base font-medium text-ash"
                >
                  Sign in →
                </a>
              ) : (
                <Link
                  href="/request-access"
                  onClick={() => setMobileOpen(false)}
                  className="block rounded-pill px-5 py-3 text-center text-base font-semibold"
                  style={{ background: '#7CFFB8', color: '#0E1316' }}
                >
                  Request access
                </Link>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
